import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/*
 * shadcn-style button with Dashkit visual parity (spec 2026-08-12).
 * Metrics mirror Dashkit's .btn / .btn-sm; variants mirror .btn-primary,
 * .btn-outline-secondary and .btn-link. Keep hex values in sync with
 * src/vendor/dashkit/css/theme.bundle.css until the new design system
 * replaces the token file.
 */
const buttonVariants = cva(
  'tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:border tw:border-transparent tw:font-normal tw:transition-colors tw:cursor-pointer tw:disabled:pointer-events-none tw:disabled:opacity-65 tw:focus-visible:outline-none tw:focus-visible:ring-[0.15rem]',
  {
    variants: {
      variant: {
        primary:
          'tw:bg-primary tw:text-primary-foreground tw:border-primary tw:hover:bg-[#155867] tw:hover:border-[#145361] tw:active:bg-[#145361] tw:active:border-[#134e5b] tw:focus-visible:ring-[rgba(60,127,141,0.5)] tw:disabled:bg-[var(--bs-flair-bg-gray-2)] tw:disabled:text-[var(--bs-flair-gray-3)] tw:disabled:border-transparent',
        'outline-secondary':
          'tw:bg-transparent tw:text-[#8d8c7d] tw:border-[#8d8c7d] tw:hover:bg-[#8d8c7d] tw:hover:text-white tw:active:bg-[#8d8c7d] tw:active:text-white tw:focus-visible:ring-[rgba(141,140,125,0.5)]',
        link: 'tw:bg-transparent tw:text-[var(--bs-link-color)] tw:hover:text-[var(--bs-link-hover-color)] tw:focus-visible:ring-[rgba(60,127,141,0.5)]',
        destructive:
          'tw:bg-destructive tw:text-destructive-foreground tw:border-destructive tw:hover:opacity-90 tw:focus-visible:ring-[rgba(196,16,15,0.5)]',
      },
      size: {
        md: 'tw:px-3 tw:py-2 tw:text-[0.9375rem] tw:leading-normal tw:rounded-md',
        sm: 'tw:px-2 tw:py-0.5 tw:text-[0.8125rem] tw:leading-[1.75] tw:rounded-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        // Bootstrap buttons default to type="button"; native default is
        // "submit", which would break forms during migration.
        type={asChild ? type : type ?? 'button'}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
